<template>
  <div class="kf-field" :class="{ 'kf-field--compact': compact }">
    <!-- A pinned control sits in the 52px row, where a label above it would be
         the thing that made the row 60px. Its placeholder names it instead, and
         aria-label carries the name for a screen reader. -->
    <span v-if="!compact" class="kf-field__label">{{ config.label }}</span>

    <!-- The escape hatch: a control the schema cannot express -- an async
         autocomplete, an infinite-scroll picker -- is passed down whole. -->
    <slot v-if="config.slot" name="field"></slot>

    <MultiSelect
      v-else-if="config.type === 'multiselect'"
      :value="arrayValue"
      :items="config.options || []"
      :item-text="config.itemText"
      :item-value="config.itemValue"
      :placeholder="placeholder"
      :aria-label="config.label"
      @input="commit"
      @change="$emit('change', config)"
    />

    <DateField
      v-else-if="config.type === 'date-range'"
      :value="arrayValue"
      range
      :max="config.max"
      :aria-label="config.label"
      @input="commit"
      @change="$emit('change', config)"
    />

    <DateField
      v-else-if="config.type === 'date'"
      :value="model[config.key]"
      :max="config.max"
      :aria-label="config.label"
      @input="commit"
      @change="$emit('change', config)"
    />

    <v-text-field
      v-else-if="config.type === 'text' || config.type === 'number'"
      :value="model[config.key]"
      :type="config.type === 'number' ? 'number' : 'text'"
      :placeholder="placeholder"
      :aria-label="config.label"
      dense
      outlined
      single-line
      hide-details
      clearable
      @input="commit"
      @change="$emit('change', config)"
    />

    <v-select
      v-else
      :value="model[config.key]"
      :items="config.options || []"
      :item-text="config.itemText"
      :item-value="config.itemValue"
      :return-object="!!config.returnObject"
      :placeholder="placeholder"
      :aria-label="config.label"
      menu-props="auto"
      dense
      outlined
      single-line
      hide-details
      clearable
      @change="onSelect"
    />
  </div>
</template>

<script>
import MultiSelect from "./MultiSelect.vue";
import DateField from "./DateField.vue";

/**
 * One filter rendered from its {@link FilterConfig} (see FilterBar.vue).
 *
 * It writes straight into the state object it is handed rather than emitting a
 * new one. That is the pattern every screen here already uses on its Vuex
 * filter module -- `filters.city = value` -- so a schema-driven screen behaves
 * exactly like the hand-written one it replaced, and no screen needs an
 * `@input` handler to keep its own copy in step.
 */
export default {
  name: "KfFilterField",
  components: { MultiSelect, DateField },
  props: {
    config: { type: Object, required: true },
    /** The screen's filter-state object, written to in place. */
    model: { type: Object, required: true },
    /** Pinned into the compact row: no label, and a fixed track width. */
    compact: { type: Boolean, default: false },
  },
  computed: {
    placeholder() {
      // An empty box reads as broken; "Any <thing>" reads as "not filtering".
      if (this.config.placeholder) return this.config.placeholder;
      return this.compact ? "Any " + this.config.label.toLowerCase() : "Any";
    },
    /** Vuetify's `clearable` hands back null, which breaks every .length. */
    arrayValue() {
      const raw = this.model[this.config.key];
      return Array.isArray(raw) ? raw : [];
    },
  },
  methods: {
    commit(value) {
      this.model[this.config.key] = value;
    },
    // v-select fires `change` with the new value, so writing and notifying are
    // the same event -- unlike the other controls, which do it in two steps.
    onSelect(value) {
      this.commit(value);
      this.$emit("change", this.config);
    },
  },
};
</script>

<style scoped>
/* A pinned field is one control on one line, sized by its track in the row. */
.kf-field--compact {
  display: block;
}
</style>
